import { matchTemplate, toHalfGray } from './bannerMatcher';
import { WICKELINE_TEMPLATE } from './wickelineTemplate';

// 위클라인 처치 배너 감지. 누가 처치했는지는 필요 없어서 처치한 쪽(왼쪽 사진)은 보지 않는다. 세 가지 모양을 따로 비교한다.
// - 위클라인 사진(오른쪽, 불투명한 부분): 배경 장면에 덜 흔들려 이것만 맞아도 위클라인 처치로 본다.
// - 전체 모양(가운데 아이콘 + `위클라인 처치` 글자 + 위클라인 사진): 영상 장면에서 가장 확실하지만 반투명한 부분이 장면에 따라 달라 보일 수 있다.
// - 가운데 처치 아이콘: 알파/오메가/위클라인 등 모든 처치 배너가 같은 아이콘이라(영상에서 0.997 이상) "어떤 처치 알림이든 떴다"는 신호다.
//   위클라인으로 인식하지 못했을 때 처치 알림 프레임을 모아 원인을 보려는 진단에 쓴다.
// 같은 파란 배너를 쓰는 구역 알림 등은 아이콘이 없어 처치 알림으로 보지 않는다(영상 전 구간 496장 확인).

export const WICKELINE_REGION = { x: 0, y: 640, width: 560, height: 150 }; // 기준 해상도(높이 1440)의 원본 좌표
export const WICKELINE_THRESHOLD = 0.8; // 전체 모양: 처치 배너는 0.98 이상, 다른 장면은 0.6 이하
export const PORTRAIT_THRESHOLD = 0.85; // 위클라인 사진만: 처치 배너 0.99 이상, 다른 장면 0.55 이하
export const ICON_THRESHOLD = 0.9; // 처치 아이콘: 처치 배너 0.997, 구역 알림/평소 화면은 훨씬 낮음

/**
 * 화면(video) 크기에서 알림 영역을 오려 올 원본 픽셀 위치. HUD는 화면 왼쪽 기준, 크기는 화면 높이 기준(높이 1440 = 배율 1)이다.
 * 오려 온 영역은 WICKELINE_REGION 크기(560×150)로 맞춰 읽는다.
 */
export function wickelineRegionRect(videoWidth, videoHeight) {
  const scale = videoHeight / 1440;
  return {
    sx: Math.round(WICKELINE_REGION.x * scale),
    sy: Math.round(WICKELINE_REGION.y * scale),
    sw: Math.round(WICKELINE_REGION.width * scale),
    sh: Math.round(WICKELINE_REGION.height * scale),
  };
}

// base64 → 바이트. (브라우저는 atob, node(Jest)는 Buffer)
const decodeBase64 = (text) => {
  if (typeof atob === 'function') {
    return Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
  }
  return new Uint8Array(Buffer.from(text, 'base64'));
};

// 저장된 전체 모양(절반 크기 밝기 이미지)에서 부분 모양을 잘라 쓴다. 좌표는 감지 영역(560×150)의 절반 크기 좌표다.
const crop = (full, x, y, width, height) => {
  const data = new Uint8Array(width * height);
  for (let row = 0; row < height; row += 1) {
    for (let col = 0; col < width; col += 1) {
      data[row * width + col] = full.data[(y - full.y + row) * full.width + (x - full.x + col)];
    }
  }
  return { width, height, data, x, y };
};

let templates = null;
const getTemplates = () => {
  if (!templates) {
    const full = { ...WICKELINE_TEMPLATE, data: decodeBase64(WICKELINE_TEMPLATE.base64) };
    templates = {
      full,
      portrait: crop(full, 167, 14, 68, 51), // 위클라인 사진 (기준 해상도 x 335~470)
      icon: crop(full, 120, 20, 36, 30), // 가운데 처치 아이콘 (기준 해상도 x 240~312, y 40~100)
    };
  }
  return templates;
};

// 어긋남 허용 범위(절반 크기 픽셀): 전체 모양은 ±6, 작은 부분 모양은 ±12(기준 해상도 약 ±24px). 창 모드 제목 표시줄 등으로 위치가 밀려도 찾는다.
const FULL_RANGE = 6;
const PART_RANGE = 12;

/**
 * 알림 영역 이미지(560×150 RGBA) → { matched, killBanner, score, portraitScore, iconScore }.
 * - matched: 위클라인 처치 배너(전체 모양 또는 위클라인 사진이 맞음)
 * - killBanner: 어떤 대상이든 처치 배너가 떠 있음(처치 아이콘이 맞음)
 * - score/portraitScore/iconScore: 각 모양과의 NCC(−1~1)
 */
export function detectWickelineBanner(image) {
  const gray = toHalfGray(image);
  const { full, portrait, icon } = getTemplates();
  const score = matchTemplate(gray, full, FULL_RANGE);
  const portraitScore = matchTemplate(gray, portrait, PART_RANGE);
  const iconScore = matchTemplate(gray, icon, PART_RANGE);
  return {
    matched: score >= WICKELINE_THRESHOLD || portraitScore >= PORTRAIT_THRESHOLD,
    killBanner: iconScore >= ICON_THRESHOLD,
    score,
    portraitScore,
    iconScore,
  };
}
